import { memo } from 'react'
import { Box, TableRow, TableCell, Typography, Chip, IconButton } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import VisibilityIcon from '@mui/icons-material/Visibility'
import StorageIcon from '@mui/icons-material/Storage'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CachedImage from '../CachedImage'
import {
  formatVatPercentDisplay,
  formatDsdDisplay,
  getStatusColor,
} from '../../utils/productManagementUtils'

function ProductTableRow({
  product,
  onView,
  onEdit,
  onDelete,
  onInventory,
  onCopySku,
  onNavigateToProduct,
}) {
  const imageSrc =
    product.image_url?.logo || product.image_url?.portrait || product.image_url?.banner

  return (
    <TableRow
      sx={{
        '&:hover': { bgcolor: '#f8f9fb' },
        transition: 'background-color 0.2s ease',
      }}
    >
      <TableCell sx={{ textAlign: 'center' }}>
        {imageSrc ? (
          <CachedImage
            src={imageSrc}
            alt={product.product_name}
            style={{
              height: '60px',
              width: '60px',
              objectFit: 'cover',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
            sx={{ '&:hover': { opacity: 0.8 } }}
            onClick={() => onNavigateToProduct(product.id)}
          />
        ) : (
          <Box
            sx={{
              height: '60px',
              width: '60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#eee',
              borderRadius: '4px',
              color: '#999',
              fontSize: '0.7rem',
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': { bgcolor: '#ddd' },
            }}
            onClick={() => onNavigateToProduct(product.id)}
          >
            No img
          </Box>
        )}
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ color: '#666' }}>
            {product.sku}
          </Typography>
          <IconButton
            size="small"
            onClick={() => onCopySku(product.sku)}
            sx={{
              p: 0.5,
              color: '#666',
              '&:hover': { color: '#e90000' },
            }}
          >
            <ContentCopyIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </TableCell>
      <TableCell
        sx={{
          fontWeight: 600,
          cursor: 'pointer',
          '&:hover': { color: '#1976d2' },
        }}
        onClick={() => onNavigateToProduct(product.id)}
      >
        {product.product_name}
      </TableCell>
      <TableCell sx={{ color: '#666' }}>{product.brand || '-'}</TableCell>
      <TableCell sx={{ color: '#666' }}>{formatVatPercentDisplay(product.vat)}</TableCell>
      <TableCell sx={{ color: '#666' }}>{formatDsdDisplay(product.dsd)}</TableCell>
      <TableCell sx={{ color: '#666', maxWidth: 160 }}>
        <Typography variant="body2" noWrap title={product.tp_map || ''}>
          {product.tp_map || '—'}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={product.category_name || 'Uncategorized'}
          size="small"
          sx={{ bgcolor: '#f0f0f0', fontWeight: 500 }}
        />
      </TableCell>
      <TableCell>
        <Chip
          label={product.status}
          color={getStatusColor(product.status)}
          size="small"
          variant="outlined"
        />
        {product.is_purchasable === false && (
          <Chip
            label="Not purchasable"
            size="small"
            sx={{
              ml: 0.5,
              backgroundColor: '#ffebee',
              color: '#c62828',
              fontWeight: 500,
            }}
          />
        )}
      </TableCell>
      <TableCell align="center" sx={{ minWidth: 140 }}>
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'nowrap' }}>
          <IconButton
            size="small"
            onClick={() => onView(product)}
            title="View Details"
            sx={{ color: '#e90000' }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onInventory(product)}
            title="View Inventory"
            sx={{ color: '#1976d2' }}
          >
            <StorageIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onEdit(product)}
            title="Edit"
            sx={{ color: '#1976d2' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onDelete(product)}
            title="Delete"
            sx={{ color: '#d32f2f' }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </TableCell>
    </TableRow>
  )
}

export default memo(ProductTableRow)
